import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { insights } from '@repletoryreach/sdk-api';
import { Button, Chip, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { SegmentedControl, Textarea, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { useI18n } from '../../i18n/I18nProvider';
import { formatCredits } from '../../lib/credits';
import { formatCount } from '../../lib/format';
import { addDays, todayIn } from '../bookings/lib/time';
import { useOrgTimezone } from '../bookings/lib/data';
import { CHANNELS, INSIGHTS_KEY, PRESETS, focusProblem, insightErrorText, parseFocus, periodProblem } from './lib/data';
import { MUTED, channelLabel, depthLabel } from './shared';

export interface ReportDraft {
  from?: string;
  to?: string;
  channels?: string[];
  depth?: insights.InsightDepth;
  focus?: string[];
}

type Preset = `${(typeof PRESETS)[number]}` | 'custom';

/**
 * Start a report (owner, 29 October 2026): the period, the channels, how deep it reads and the business's own
 * questions, each depth with its models and price, and a plain notice that it takes a few minutes, reads the chats
 * with AI, is charged to the credits, and that an email and a notification say when it is ready.
 */
export function NewReportModal({ opened, onClose, draft, onStarted }: {
  opened: boolean; onClose: () => void; draft?: ReportDraft | null; onStarted?: (r: insights.InsightReportSummary) => void;
}) {
  const { t, locale } = useI18n();
  const tz = useOrgTimezone();
  const today = todayIn(tz);
  const qc = useQueryClient();
  const [seeded, setSeeded] = useState<ReportDraft | null | undefined>(undefined);
  const [preset, setPreset] = useState<Preset>('30');
  const [from, setFrom] = useState(() => addDays(today, -29));
  const [to, setTo] = useState(today);
  const [channels, setChannels] = useState<string[]>([]);
  const [depth, setDepth] = useState<insights.InsightDepth>('standard');
  const [focusText, setFocusText] = useState('');
  // Run again opens this with the report's own settings.
  if (opened && draft !== seeded) {
    setSeeded(draft);
    if (draft?.from && draft.to) { setFrom(draft.from); setTo(draft.to); setPreset('custom'); }
    else { setPreset('30'); setFrom(addDays(today, -29)); setTo(today); }
    setChannels(draft?.channels ?? []);
    setDepth(draft?.depth ?? 'standard');
    setFocusText((draft?.focus ?? []).join('\n'));
  }

  function choose(p: Preset) {
    setPreset(p);
    if (p !== 'custom') { setFrom(addDays(today, -(Number(p) - 1))); setTo(today); }
  }

  const periodError = periodProblem(from, to, today);
  const focus = parseFocus(focusText);
  const focusError = focusProblem(focus);
  const estimate = useQuery({
    queryKey: [INSIGHTS_KEY, 'estimate', from, to, channels.join(','), focus.length],
    queryFn: () => insights.estimateInsights({ from, to, channels, focus: focus.length }),
    enabled: opened && !periodError,
    staleTime: 30_000
  });
  const e = estimate.data?.data;
  const quote = e?.depths.find((d) => d.depth === depth);
  const amount = (minor: number) => formatCredits(minor, t, e?.currency);

  const start = useMutation({
    // The report is written in the console's language when that is English or Afrikaans.
    mutationFn: () => insights.startInsightReport({ from, to, channels, depth, focus, language: locale === 'af' ? 'af' : 'en' }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: [INSIGHTS_KEY] });
      notifySuccess(t('insights.new.started'), t('insights.title'));
      onClose();
      onStarted?.(res.data);
    }
  });

  const ready = !!e && !!quote && quote.available && e.readable > 0 && (!e.priced || quote.affordable) && !periodError && !focusError;

  return (
    <Modal opened={opened} onClose={onClose} title={t('insights.new.title')} size="lg">
      <Stack gap="md" data-testid="insights-new" data-guide="insights-new-form">
        <Stack gap="xs">
          <Text role="label">{t('insights.new.period.label')}</Text>
          <div className="rr-insights__scroll-x">
            <SegmentedControl size="sm" value={preset} onChange={(v) => choose(v as Preset)}
              data={[...PRESETS.map((n) => ({ value: String(n), label: t('insights.new.period.last', { n }) })), { value: 'custom', label: t('insights.new.period.custom') }]} />
          </div>
          <Group gap="sm" wrap="wrap" align="flex-start">
            <TextInput size="sm" type="date" label={t('insights.new.period.from')} value={from} max={to || today}
              onChange={(ev) => { setFrom(ev.currentTarget.value); setPreset('custom'); }} />
            <TextInput size="sm" type="date" label={t('insights.new.period.to')} value={to} min={from} max={today}
              onChange={(ev) => { setTo(ev.currentTarget.value); setPreset('custom'); }}
              error={periodError ? t(periodError, { max: 92 }) : undefined} />
          </Group>
        </Stack>

        <Stack gap="xs">
          <Text role="label">{t('insights.new.channels.label')}</Text>
          <Group gap="xs" wrap="wrap" role="group" aria-label={t('insights.new.channels.label')}>
            {CHANNELS.map((c) => (
              <Chip key={c} size="sm" checked={channels.includes(c)}
                onChange={(on) => setChannels((cur) => (on ? [...cur, c] : cur.filter((x) => x !== c)))}>
                {channelLabel(t, c)}
              </Chip>
            ))}
          </Group>
          <Text role="caption" c={MUTED}>{t('insights.new.channels.hint')}</Text>
        </Stack>

        {periodError ? null : estimate.isPending ? <Skeleton height={120} /> : estimate.error ? (
          <Alert color="error">{insightErrorText(t, estimate.error)}</Alert>
        ) : e ? (
          <Stack gap="xs" data-testid="insights-estimate">
            <Text role="bodySmall">
              {t('insights.new.estimate.chats', { chats: formatCount(e.chats), readable: formatCount(e.readable) })}
            </Text>
            {e.reused > 0 && <Text role="bodySmall" c={MUTED}>{t('insights.new.estimate.reused', { count: formatCount(e.reused) })}</Text>}
            {e.sampled && <Text role="bodySmall" c={MUTED}>{t('insights.new.estimate.sampled', { read: formatCount(e.to_read), max: formatCount(e.max_chats) })}</Text>}
            {e.readable === 0 && <Alert color="warning">{t('insights.error.nothing')}</Alert>}
          </Stack>
        ) : null}

        {e && (
          <Stack gap="xs">
            <Text role="label" id="insights-depth-label">{t('insights.new.depth.label')}</Text>
            <div className="rr-insights__depths" role="radiogroup" aria-labelledby="insights-depth-label">
              {insights.INSIGHT_DEPTHS.map((d) => {
                const q = e.depths.find((x) => x.depth === d);
                if (!q) return null;
                const chosen = depth === d;
                return (
                  <button key={d} type="button" role="radio" aria-checked={chosen} disabled={!q.available}
                    className={`rr-insights__depth${chosen ? ' is-chosen' : ''}`} onClick={() => setDepth(d)} data-testid={`insights-depth-${d}`}>
                    <Text role="bodySmall" fw={600}>{depthLabel(t, d)}{d === 'standard' ? ` · ${t('insights.depth.recommended')}` : ''}</Text>
                    <Text role="caption" c={MUTED}>{t(`insights.depth.${d}.about`)}</Text>
                    <Text role="caption">{t('insights.depth.models', { reader: q.reader_model, writer: q.writer_model })}</Text>
                    {q.available ? (
                      <Text role="bodySmall" fw={600} data-testid={`insights-price-${d}`}>
                        {e.priced ? t('insights.depth.price', { amount: amount(q.estimated_minor) }) : t('insights.depth.unpriced')}
                      </Text>
                    ) : <Text role="caption" c={MUTED}>{t('insights.depth.unavailable')}</Text>}
                    {q.available && q.fallback && <Text role="caption" c={MUTED}>{t('insights.depth.fallback')}</Text>}
                    {q.available && e.priced && !q.affordable && <Text role="caption" c="var(--rr-color-error)">{t('insights.depth.short')}</Text>}
                  </button>
                );
              })}
            </div>
          </Stack>
        )}

        <Textarea label={t('insights.new.focus.label')} description={t('insights.new.focus.hint')} value={focusText}
          minRows={2} autosize onChange={(ev) => setFocusText(ev.currentTarget.value)} error={focusError ? t(focusError) : undefined}
          data-testid="insights-focus" />

        {e && quote?.available && (
          <Alert color="info" data-testid="insights-notice">
            {t('insights.new.notice', {
              minutes: formatCount(Math.max(1, e.minutes)),
              amount: e.priced ? amount(quote.estimated_minor) : t('insights.new.noticeUnpriced')
            })}
          </Alert>
        )}
        {start.error && <Alert color="error" role="alert">{insightErrorText(t, start.error)}</Alert>}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
          <Button onClick={() => start.mutate()} loading={start.isPending} disabled={!ready} data-testid="insights-start">
            {t('insights.new.start')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
